import { Box, Card, CardContent, Skeleton, Stack, Typography } from '@mui/material';

/**
 * StatCard — KPI tile with label, value, caption, and optional icon.
 *
 * @param {string}    label     — Metric name (small, above the value)
 * @param {ReactNode} value     — The metric value
 * @param {ReactNode} [caption] — Supporting line under the value
 * @param {ReactNode} [icon]    — MUI icon element, shown in a soft tile at right
 * @param {string}    [color]   — Palette color for value + icon (e.g. 'success.main')
 * @param {boolean}   [loading] — Render skeleton placeholders
 * @param {function}  [onClick] — Makes the card interactive (hover lift)
 * @param {object}    [sx]      — Card overrides
 */
const StatCard = ({ label, value, caption, icon, color, loading, onClick, sx }) => (
  <Card
    onClick={onClick}
    sx={{
      height: '100%',
      ...(onClick && {
        cursor: 'pointer',
        transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 16px rgba(16, 24, 40, 0.10)',
          borderColor: 'primary.main',
        },
      }),
      ...sx,
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.68rem' }}
            noWrap
          >
            {label}
          </Typography>
          {loading ? (
            <Skeleton width={90} height={34} />
          ) : (
            <Typography
              variant="h4"
              fontWeight={700}
              color={color}
              className="tabular-nums"
              sx={{ lineHeight: 1.3, mt: 0.25 }}
            >
              {value}
            </Typography>
          )}
          {caption && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {loading ? <Skeleton width={70} /> : caption}
            </Typography>
          )}
        </Box>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 2,
              flexShrink: 0,
              color: color || 'primary.main',
              bgcolor: 'action.hover',
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

export default StatCard;
